import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { humanize, timeOnly } from '@/lib/utils';
import { Alert, Badge, Button, Card, CardContent, TableSkeleton } from '@/components/ui/primitives';

interface BoardDoctor {
  doctor: { id: string; name: string; designation: string | null };
  sessions: { startsAt: string; endsAt: string; slotMinutes: number; capacity: number; room: string | null }[];
  blocks: { startsAt: string; endsAt: string; reason: string }[];
  appointments: { id: string; slotAt: string; status: string; tokenNo: string | null; isWalkIn: boolean; initials: string }[];
}

const STATUS_TONE: Record<string, 'secondary' | 'success' | 'warning' | 'muted' | 'destructive'> = {
  booked: 'secondary',
  confirmed: 'secondary',
  arrived: 'warning',
  in_consultation: 'warning',
  completed: 'success',
  no_show: 'destructive',
};

export function DayBoard({ branchId, date }: { branchId: string; date: string }) {
  const [page, setPage] = useState(1);
  const board = useQuery({
    queryKey: ['day-board', branchId, date, page],
    queryFn: () => get<{ doctors: BoardDoctor[]; hasMore: boolean }>(`/schedules/day-board${qs({ branchId, date, page })}`),
    enabled: Boolean(branchId),
  });

  if (!branchId) return <Alert variant="warning">{tr('opd', 'your_account_has_no_branch_ask_an_administrator')}</Alert>;
  if (board.isLoading) return <TableSkeleton rows={5} />;
  if (!board.data?.doctors.length) return <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">{tr('opd', 'no_clinics_on_this_day')}</p>;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {board.data.doctors.map((row) => (
          <Card key={row.doctor.id}>
            <CardContent className="space-y-2 pt-4 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold">{row.doctor.name}</p>
                <span className="text-xs text-muted-foreground">
                  {row.sessions.map((session) => `${timeOnly(session.startsAt)}–${timeOnly(session.endsAt)}${session.room ? ` · ${session.room}` : ''}`).join(', ')}
                </span>
              </div>
              {row.blocks.map((block) => (
                <Badge key={block.startsAt} variant="destructive">
                  {humanize(block.reason)} {timeOnly(block.startsAt)}–{timeOnly(block.endsAt)}
                </Badge>
              ))}
              <div className="flex flex-wrap gap-1">
                {row.appointments.map((appointment) => (
                  <Badge key={appointment.id} variant={STATUS_TONE[appointment.status] ?? 'muted'} title={appointment.status}>
                    {timeOnly(appointment.slotAt)} {appointment.initials}
                    {appointment.tokenNo ? ` #${appointment.tokenNo}` : ''}
                    {appointment.isWalkIn ? ' W' : ''}
                  </Badge>
                ))}
                {row.appointments.length === 0 && <span className="text-muted-foreground">{tr('opd', 'no_bookings')}</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        {page > 1 && (
          <Button variant="outline" size="sm" onClick={() => setPage(page - 1)}>
            {tr('opd', 'previous')}
          </Button>
        )}
        {board.data.hasMore && (
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)}>
            {tr('opd', 'next_doctors')}
          </Button>
        )}
      </div>
    </div>
  );
}
